import { DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Chart } from 'chart.js';
import { Subject } from 'rxjs';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';

@Component({
  selector: 'app-cp-details-dialog',
  templateUrl: './cp-details-dialog.component.html',
  styleUrls: ['./cp-details-dialog.component.scss'],

  standalone: false,
})
export class CPDetailsDialogComponent {
  today: Date = new Date();
  private chart: Chart | null = null;
  chartData: { label: string; value: number }[] = [];
  private destroy$ = new Subject<void>();
  branchId: string | null = null;
  implementationName: string | null = null;
  clientId: string | null = null;
  mappingType: number | null = null;
  totalActiveLicenses: number | null = null;
  newLicensesData: { label: string; value: number }[] = [];
  renewingLicensesData: { label: string; value: number }[] = [];
  totalNewLicensesThisMonth: number = 0;
  /* istanbul ignore next */
  constructor(
    private route: ActivatedRoute,
    public backend: BackendAccessService,
    public datePipe: DatePipe,
    private branchSelectionService: BranchSelectionService,
    private loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.handleResize = this.handleResize.bind(this);
  }
  /* istanbul ignore next */
  ngOnInit() {
    if (this.data) {
      this.implementationName = this.data.implementationName;
      this.branchId = this.data.branchId;
      this.clientId = this.data.clientId;
      this.mappingType = this.data.mappingType;
    }
    this.loadCPDetailsPage();
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    window.addEventListener('resize', this.handleResize);
  }
  /* istanbul ignore next */
  ngOnDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.destroy$.next();
    this.destroy$.complete();
  }
  /* istanbul ignore next */
  handleResize() {
    if (this.chart) {
      this.chart.resize();
    }
  }

  /* istanbul ignore next */
  loadCPDetailsPage() {
    this.loadingService.show();
    if (this.data) {
      this.backend
        .getCPDetailsData(this.data.clid, this.data.brid, this.data.mpt)
        .subscribe({
          next: (data) => {
            localStorage.setItem('CPDetailsData', JSON.stringify(data));
            this.totalActiveLicenses = data.totalActiveLicenses;
            this.implementationName = data.implementationName;
            this.processData(data);
            this.createChart(this.newLicensesData, this.renewingLicensesData);
            this.loadingService.hide();
          },
          error: (error) => {
            console.error('Error fetching API details data:', error);
            this.loadingService.hide();
          },
        });
    } else {
      console.error('Invalid data received in CPDetailsDialogComponent');
      this.loadingService.hide();
    }
  }
  /* istanbul ignore next */
  processData(data: any) {
    this.loadingService.show();
    const currentMonth = new Date().toLocaleString('default', {
      month: 'short',
    });
    const currentYear = new Date().getFullYear();

    if (data && data.monthlyData) {
      const currentMonthData = data.monthlyData.find(
        (item: { createdMonth: string; createdYear: number }) =>
          item.createdMonth === currentMonth && item.createdYear === currentYear
      );

      this.totalNewLicensesThisMonth = currentMonthData
        ? currentMonthData.totalNewLicenses
        : 0;

      const newLicensesData = data.monthlyData.map(
        (item: {
          createdMonth: any;
          createdYear: any;
          totalNewLicenses: any;
        }) => ({
          label: `${item.createdMonth} ${item.createdYear}`,
          value: item.totalNewLicenses,
        })
      );
      const renewingLicensesData = data.monthlyData.map(
        (item: {
          createdMonth: any;
          createdYear: any;
          totalRenewingLicenses: any;
        }) => ({
          label: `${item.createdMonth} ${item.createdYear}`,
          value: item.totalRenewingLicenses,
        })
      );
      this.newLicensesData = newLicensesData;
      this.renewingLicensesData = renewingLicensesData;
    } else {
      console.warn('Data is not available for chart processing');
    }
    this.loadingService.hide();
  }

  createChart(newLicensesData: any, renewingLicensesData: any) {
    // Check if the data arrays are null or undefined
    if (!newLicensesData || !renewingLicensesData) {
      console.error('Data not available for creating chart.');
      return; // Exit the function if data is missing
    }

    const canvas = document.getElementById('CPChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const labels = newLicensesData.map(
      (dataItem: { label: any }) => dataItem.label
    );

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Renewing Licenses',
            data: renewingLicensesData.map(
              (dataItem: { value: any }) => dataItem.value
            ),
            backgroundColor: 'rgba(255, 99, 132, 0.4)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
          },
          {
            label: 'New Licenses Added',
            data: newLicensesData.map(
              (dataItem: { value: any }) => dataItem.value
            ),
            backgroundColor: 'rgba(29, 78, 216, 0.2)',
            borderColor: 'rgba(29, 78, 216, 1)',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true,
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
              stacked: true,
            },
          ],
        },
      },
    });
  }
}
